import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, MapPin, Clock } from "lucide-react";

interface Props {
  ownerId: string;
}

interface Arrival {
  booking_id: string;
  start_time: string;
  end_time: string;
  address: string;
  checked_in_at: string | null;
  checked_out_at: string | null;
  eta_minutes: number | null;
  eta_updated_at: string | null;
  latitude: number | null;
  longitude: number | null;
}

const ArrivalsTab = ({ ownerId }: Props) => {
  const [loading, setLoading] = useState(true);
  const [arrivals, setArrivals] = useState<Arrival[]>([]);

  const load = async () => {
    const { data: spots } = await supabase
      .from("parking_spots")
      .select("id, address")
      .eq("owner_id", ownerId);
    const ids = (spots ?? []).map((s) => s.id);
    if (!ids.length) {
      setArrivals([]);
      setLoading(false);
      return;
    }
    const addressMap = new Map((spots ?? []).map((s) => [s.id, s.address]));
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);

    const { data: bk } = await supabase
      .from("bookings")
      .select("id, start_time, end_time, parking_spot_id, checked_in_at, checked_out_at, status")
      .in("parking_spot_id", ids)
      .gte("start_time", startOfDay.toISOString())
      .lte("start_time", endOfDay.toISOString())
      .neq("status", "cancelled")
      .order("start_time", { ascending: true });

    const bookingIds = (bk ?? []).map((b) => b.id);
    let etaMap = new Map<string, { eta: number | null; updated: string; lat: number; lng: number }>();
    if (bookingIds.length) {
      const { data: etas } = await supabase
        .from("booking_eta_updates")
        .select("booking_id, eta_minutes, updated_at, latitude, longitude")
        .in("booking_id", bookingIds)
        .order("updated_at", { ascending: false });
      (etas ?? []).forEach((e) => {
        if (!etaMap.has(e.booking_id))
          etaMap.set(e.booking_id, {
            eta: e.eta_minutes,
            updated: e.updated_at,
            lat: Number(e.latitude),
            lng: Number(e.longitude),
          });
      });
    }

    setArrivals(
      (bk ?? []).map((b) => {
        const e = etaMap.get(b.id);
        return {
          booking_id: b.id,
          start_time: b.start_time,
          end_time: b.end_time,
          address: addressMap.get(b.parking_spot_id) ?? "",
          checked_in_at: b.checked_in_at,
          checked_out_at: b.checked_out_at,
          eta_minutes: e?.eta ?? null,
          eta_updated_at: e?.updated ?? null,
          latitude: e?.lat ?? null,
          longitude: e?.lng ?? null,
        };
      })
    );
    setLoading(false);
  };

  useEffect(() => {
    load();
    const channel = supabase
      .channel("arrivals-eta")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "booking_eta_updates" }, () =>
        load()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [ownerId]);

  if (loading)
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  if (!arrivals.length)
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          No arrivals scheduled for today.
        </CardContent>
      </Card>
    );

  return (
    <div className="space-y-4">
      {arrivals.map((a) => (
        <Card key={a.booking_id}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> {a.address}
                </CardTitle>
                <CardDescription>
                  {new Date(a.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{" "}
                  → {new Date(a.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </CardDescription>
              </div>
              <div className="flex flex-col items-end gap-1">
                {a.checked_out_at ? (
                  <Badge variant="secondary">Checked out</Badge>
                ) : a.checked_in_at ? (
                  <Badge>Checked in</Badge>
                ) : (
                  <Badge variant="outline">Awaiting</Badge>
                )}
                {a.eta_minutes != null && !a.checked_in_at && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" /> ETA {a.eta_minutes} min
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
};

export default ArrivalsTab;
